import React, { useState } from 'react';
import { X, Edit2, Trash2, UserCheck, Link as LinkIcon } from 'lucide-react';
import { doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { CalendarEvent } from '../../types/calendar';
import { format, parseISO, isValid } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface EventDetailsModalProps {
  event: CalendarEvent;
  onClose: () => void;
  onDelete?: () => void;
}

export default function EventDetailsModal({ event, onClose, onDelete }: EventDetailsModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(event);
  const navigate = useNavigate();

  const canEdit = auth.currentUser?.uid === event.createdBy;

  const formatDate = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) {
        return 'Invalid date';
      }
      return format(date, 'PPp');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Event title is required');
      return;
    }

    try {
      setLoading(true);
      await updateDoc(doc(db, 'events', event.id), {
        ...formData,
        updatedAt: serverTimestamp(),
      });
      toast.success('Event updated successfully');
      onClose();
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Failed to update event');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'events', event.id));
      toast.success('Event deleted successfully');
      if (onDelete) {
        onDelete();
      }
      onClose();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    } finally {
      setLoading(false);
    }
  };

  const handleTakeAttendance = () => {
    navigate(`/attendance?eventId=${event.id}`);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-light rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border-light">
          <h2 className="text-xl font-semibold text-text-primary">
            {isEditing ? 'Edit Event' : 'Event Details'}
          </h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Start
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.start}
                    onChange={(e) => setFormData({ ...formData, start: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                             focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    End
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.end}
                    onChange={(e) => setFormData({ ...formData, end: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                             focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location || ''}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                  placeholder="Add location (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as CalendarEvent['type'] })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                >
                  <option value="event">Event</option>
                  <option value="task">Task</option>
                  <option value="reminder">Reminder</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Visibility
                </label>
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value as CalendarEvent['visibility'] })}
                  className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                           focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.attendanceRequired}
                    onChange={(e) => setFormData({ ...formData, attendanceRequired: e.target.checked })}
                    className="w-4 h-4 text-primary-500 border-border-light rounded 
                             focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-text-secondary">
                    Require Attendance
                  </span>
                </label>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-2">{event.title}</h3>
                {event.description && (
                  <p className="text-text-secondary whitespace-pre-wrap">{event.description}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-1">Start</h4>
                  <p className="text-text-primary">{formatDate(event.start)}</p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-1">End</h4>
                  <p className="text-text-primary">{formatDate(event.end)}</p>
                </div>
              </div>

              {event.location && (
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-1">Location</h4>
                  <p className="text-text-primary">{event.location}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-1">Type</h4>
                  <p className="text-text-primary capitalize">{event.type}</p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-1">Visibility</h4>
                  <p className="text-text-primary capitalize">{event.visibility}</p>
                </div>
              </div>

              {/* Attendance Section */}
              {event.attendanceRequired && (
                <div className="mt-6 pt-6 border-t border-border-light">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-medium text-text-primary">Attendance Required</h4>
                      <p className="text-sm text-text-secondary">Track attendance for this event</p>
                    </div>
                    <button
                      onClick={handleTakeAttendance}
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                               transition-colors flex items-center gap-2"
                    >
                      <UserCheck className="w-5 h-5" />
                      Take Attendance
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <LinkIcon className="w-4 h-4" />
                    <span>Linked to attendance system</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-border-light">
            {showDeleteConfirm ? (
              <div className="bg-error-light/20 p-4 rounded-lg">
                <p className="text-error-dark mb-4">
                  Are you sure you want to delete this event? This action cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={loading}
                    className="px-4 py-2 bg-error-DEFAULT text-white rounded-lg hover:bg-error-dark 
                             transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Delete Event
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex justify-between">
                {canEdit && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-error-DEFAULT hover:text-error-dark transition-colors 
                             flex items-center gap-2"
                  >
                    <Trash2 className="w-5 h-5" />
                    Delete
                  </button>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Close
                  </button>
                  {canEdit && (
                    isEditing ? (
                      <button
                        onClick={handleSave}
                        disabled={loading}
                        className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                                 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Saving...' : 'Save Changes'}
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 text-primary-500 hover:text-primary-600 transition-colors 
                                 flex items-center gap-2"
                      >
                        <Edit2 className="w-5 h-5" />
                        Edit
                      </button>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}